import classNames from "classnames";
import { useSiteTextContent } from "@Front/Stores/SiteTextsContext";

import Typography, { ETypo, ETypoColor } from "../Typography";
import classes from "./classes.module.scss";
import DropdownMenu from "./DropdownMenu";
import { IOption } from "./DropdownMenu/DropdownOption";
import { useDropdownController } from "./useDropdownController";

type IProps = {
	options: IOption[];
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	onSelectionChange?: (option: IOption) => void;
	selectedOption?: IOption | null;
	className?: string;
};

export default function Dropdown(props: IProps): React.JSX.Element {
	const { options, placeholder, disabled, onSelectionChange, selectedOption: selectedOptionProps, label, className } = props;

	const { selectedOption, openable, labelIdRef, menuIdRef, handleOnSelect, handleKeyDown, ariaLabel: controllerAriaLabel } = useDropdownController({
		options,
		selectedOptionProps,
		onSelectionChange,
		disabled,
		label,
		placeholder,
	});

	const defaultAriaLabel = useSiteTextContent("components.dropdown.defaultAriaLabel");
	const ariaLabel = controllerAriaLabel === "Dropdown" ? defaultAriaLabel : controllerAriaLabel;
	return (
		<DropdownMenu
			options={options}
			openable={openable}
			onSelect={handleOnSelect}
			selectedOptions={selectedOption ? [selectedOption] : []}
			menuId={menuIdRef.current}
			listboxAccessibleName={ariaLabel}>
			<div className={classNames(classes["root"], className)}>
				{label ? <div id={labelIdRef.current} className={classes["label"]}><Typography typo={ETypo.TEXT_MD_REGULAR} color={ETypoColor.CONTRAST_DEFAULT}>{label}</Typography></div> : null}
				<DropdownCombobox
					openable={openable}
					disabled={disabled ?? false}
					selectedOption={selectedOption}
					ariaLabel={ariaLabel}
					hasLabel={!!label}
					labelIdRef={labelIdRef}
					menuIdRef={menuIdRef}
					placeholder={placeholder}
					handleKeyDown={handleKeyDown}
					onToggle={() => !disabled && openable.toggle()}
				/>
			</div>
		</DropdownMenu>
	);
}

type DropdownComboboxShellCtx = {
	openable: { isOpen: boolean };
	comboboxProps: {
		className: string;
		"aria-haspopup": "listbox";
		"aria-controls": string;
		"aria-label": string | undefined;
		"aria-labelledby": string | undefined;
		tabIndex: -1 | 0;
		onClick: () => void;
		onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
		children: React.JSX.Element;
	};
};

function dropdownComboboxShell(ctx: DropdownComboboxShellCtx): React.JSX.Element {
	const { openable, comboboxProps } = ctx;
	const { className, "aria-haspopup": ariaHaspopup, "aria-controls": ariaControls, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, tabIndex, onClick, onKeyDown, children } =
		comboboxProps;
	if (openable.isOpen) {
		return (
			<div
				className={className}
				role="combobox"
				aria-expanded="true"
				aria-haspopup={ariaHaspopup}
				aria-controls={ariaControls}
				aria-label={ariaLabel}
				aria-labelledby={ariaLabelledby}
				tabIndex={tabIndex}
				onClick={onClick}
				onKeyDown={onKeyDown}>
				{children}
			</div>
		);
	}
	return (
		<div
			className={className}
			role="combobox"
			aria-expanded="false"
			aria-haspopup={ariaHaspopup}
			aria-controls={ariaControls}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledby}
			tabIndex={tabIndex}
			onClick={onClick}
			onKeyDown={onKeyDown}>
			{children}
		</div>
	);
}

function DropdownCombobox(props: {
	openable: { isOpen: boolean };
	disabled: boolean;
	selectedOption: IOption | null;
	ariaLabel: string;
	hasLabel: boolean;
	labelIdRef: React.RefObject<string>;
	menuIdRef: React.RefObject<string>;
	placeholder?: string;
	handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
	onToggle: () => void;
}): React.JSX.Element {
	const { openable, disabled, selectedOption, ariaLabel, hasLabel, labelIdRef, menuIdRef, placeholder, handleKeyDown, onToggle } = props;
	const content = (
		<div className={classes["content"]}>
			{getLabelContent(selectedOption, placeholder)}
			<ChevronDownIcon />
		</div>
	);
	return dropdownComboboxShell({
		openable,
		comboboxProps: {
			className: classNames(
				classes["container"],
				openable.isOpen && classes["open"],
				disabled && classes["disabled"],
				!!selectedOption && classes["active"],
			),
			"aria-haspopup": "listbox",
			"aria-controls": menuIdRef.current,
			"aria-label": !hasLabel ? ariaLabel : undefined,
			"aria-labelledby": hasLabel ? labelIdRef.current : undefined,
			tabIndex: (disabled ? -1 : 0) as -1 | 0,
			onClick: onToggle,
			onKeyDown: handleKeyDown,
			children: content,
		},
	});
}

function ChevronDownIcon(): React.JSX.Element {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
			<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
		</svg>
	);
}

export function getLabel(option: IOption | null): string | null {
	if (!option) return null;
	if (typeof option.label === "string") {
		return option.label;
	}
	return `${option.label.text}, ${option.label.subtext}`;
}

function getLabelContent(option: IOption | null, placeholder?: string): React.JSX.Element {
	if (!option)
		return (
			<Typography
				className={classes["value"]}
				typo={!!option ? ETypo.TEXT_MD_SEMIBOLD : ETypo.TEXT_MD_REGULAR}
				color={!!option ? ETypoColor.DROPDOWN_CONTRAST_ACTIVE : ETypoColor.DROPDOWN_CONTRAST_DEFAULT}>
				{placeholder}
			</Typography>
		);

	if (typeof option.label === "string") {
		return (
			<Typography
				className={classes["value"]}
				typo={!!option ? ETypo.TEXT_MD_SEMIBOLD : ETypo.TEXT_MD_REGULAR}
				color={!!option ? ETypoColor.DROPDOWN_CONTRAST_ACTIVE : ETypoColor.DROPDOWN_CONTRAST_DEFAULT}>
				{option.label}
			</Typography>
		);
	}

	return (
		<span className={classes["value"]}>
			<Typography
				typo={ETypo.TEXT_MD_LIGHT}
				color={!!option ? ETypoColor.NAVIGATION_BUTTON_CONTRAST_ACTIVE : ETypoColor.NAVIGATION_BUTTON_CONTRAST_DEFAULT}>
				{`${option.label.text} , `}
			</Typography>
			<Typography
				typo={ETypo.TEXT_MD_BOLD}
				type="span"
				color={!!option ? ETypoColor.NAVIGATION_BUTTON_CONTRAST_ACTIVE : ETypoColor.NAVIGATION_BUTTON_CONTRAST_DEFAULT}>
				{option.label.subtext}
			</Typography>
		</span>
	);
}
